/**
 * YouTube Lite Facade - Performance Optimized Video Loading
 * Eliminates third-party script blocking by implementing click-to-load
 */

(() => {
  // SEC-02: Strict validation for YouTube video IDs.
  // Valid IDs are exactly 11 characters: alphanumeric, hyphens, underscores.
  // Prevents XSS via malicious data-video-id attribute values.
  const YOUTUBE_VIDEO_ID_REGEX = /^[a-zA-Z0-9_-]{11}$/;

  function isValidVideoId(id) {
    return typeof id === 'string' && YOUTUBE_VIDEO_ID_REGEX.test(id);
  }

  const YouTubeFacadeClass = class extends HTMLElement {
      constructor() {
        super();
        this.isLoaded = false;
        this.videoId = this.dataset.videoId;
        this.autoplay = this.hasAttribute("autoplay");
        this.params = this.dataset.params || "";

        // SEC-02: Validate videoId before any DOM insertion or URL construction.
        // If invalid, render a fallback placeholder instead of interpolating untrusted input.
        if (!isValidVideoId(this.videoId)) {
          console.warn('youtube-facade: invalid video ID:', this.videoId);
          this.videoId = '';
          return;
        }

        this.setupIntersectionObserver();
        this.render();
      }

      static get observedAttributes() {
        return ["data-video-id", "data-params"];
      }

      connectedCallback() {
        this._onClick = this.loadVideo.bind(this);
        this._onKeydown = this.handleKeydown.bind(this);

        this.addEventListener("click", this._onClick, { once: true });
        this.addEventListener("keydown", this._onKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this._onClick);
        this.removeEventListener("keydown", this._onKeydown);
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }

      setupIntersectionObserver() {
        if ("IntersectionObserver" in window) {
          this.intersectionObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !this.isLoaded) {
                  this.preconnectYouTube();
                }
              });
            },
            {
              rootMargin: "100px",
            }
          );

          this.intersectionObserver.observe(this);
        }
      }

      preconnectYouTube() {
        if (!document.querySelector('link[href="https://www.youtube.com"]')) {
          const preconnect1 = document.createElement("link");
          preconnect1.rel = "preconnect";
          preconnect1.href = "https://www.youtube.com";

          const preconnect2 = document.createElement("link");
          preconnect2.rel = "preconnect";
          preconnect2.href = "https://www.google.com";

          document.head.appendChild(preconnect1);
          document.head.appendChild(preconnect2);
        }
      }

      getThumbnailUrl() {
        return `https://i.ytimg.com/vi/${this.videoId}/hqdefault.jpg`;
      }

      render() {
        if (!this.videoId) return;

        const thumbnailUrl = this.getThumbnailUrl();

        // SEC-02: Use safe DOM construction instead of innerHTML with interpolated videoId.
        // This prevents XSS even if the validation regex is bypassed in the future.
        const container = document.createElement('div');
        container.className = 'youtube-facade__container';

        const thumbWrapper = document.createElement('div');
        thumbWrapper.className = 'youtube-facade__thumbnail';

        const img = document.createElement('img');
        img.src = thumbnailUrl;
        img.alt = 'Video thumbnail';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.className = 'youtube-facade__thumbnail-img';

        const playBtn = document.createElement('div');
        playBtn.className = 'youtube-facade__play-button';
        playBtn.setAttribute('aria-hidden', 'true');
        playBtn.innerHTML = '<svg viewBox="0 0 68 48" width="68" height="48"><path d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z" fill="#f00"></path><path d="M 45,24 27,14 27,34" fill="#fff"></path></svg>';

        thumbWrapper.appendChild(img);
        thumbWrapper.appendChild(playBtn);

        const info = document.createElement('div');
        info.className = 'youtube-facade__info';
        const title = document.createElement('span');
        title.className = 'youtube-facade__title';
        title.textContent = 'Click to load YouTube video';
        info.appendChild(title);

        container.appendChild(thumbWrapper);
        container.appendChild(info);

        this.textContent = '';
        this.appendChild(container);

        this.setAttribute("role", "button");
        this.setAttribute("tabindex", "0");
        this.setAttribute("aria-label", `Load YouTube video ${this.videoId}`);
      }

      handleKeydown(event) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.loadVideo();
        }
      }

      loadVideo() {
        if (this.isLoaded) return;

        this.isLoaded = true;
        this.classList.add("youtube-facade--loading");

        this.autoplay = true;

        const iframe = document.createElement("iframe");
        iframe.src = this.buildIframeSrc(true);
        iframe.allow =
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
        iframe.allowFullscreen = true;
        iframe.loading = "lazy";
        iframe.className = "youtube-facade__iframe";
        iframe.title = `YouTube video player for ${this.videoId}`;
        iframe.referrerPolicy = "strict-origin-when-cross-origin";

        iframe.onload = () => {
          this.classList.remove("youtube-facade--loading");
          this.classList.add("youtube-facade--loaded");
        };

        this.innerHTML = "";
        this.appendChild(iframe);
      }

      buildIframeSrc(forceAutoplay = false) {
        // Whitelist of allowed YouTube embed parameters
        const ALLOWED_PARAMS = new Set(['rel', 'modestbranding', 'controls', 'start', 'end', 'loop', 'mute', 'cc_load_policy', 'hl', 'fs']);

        const qp = [];

        // Parse and filter data-params through whitelist
        const rawParams = (this.params || "").replace(/^([?&])/, "");
        if (rawParams) {
          rawParams.split("&").forEach((param) => {
            const [key] = param.split("=");
            if (ALLOWED_PARAMS.has(key)) qp.push(param);
          });
        }

        qp.push("playsinline=1");
        if (forceAutoplay || this.autoplay) qp.push("autoplay=1");
        qp.push("enablejsapi=1");
        qp.push(`origin=${encodeURIComponent(window.location.origin)}`);

        const query = qp.join("&");
        return `https://www.youtube.com/embed/${this.videoId}?${query}`;
      }
    };

  if (!customElements.get("youtube-facade")) {
    customElements.define("youtube-facade", YouTubeFacadeClass);
  }
})();
